import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useSettingsStore, type Theme, MUSIC_TRACKS } from '../store/settingsStore'
import { useAudioManager } from '../hooks/useAudioManager'
import { useTheme } from '../hooks/useTheme'
import { themes } from '../design/premiumTokens'
import { ChevronLeft, ChevronRight } from 'lucide-react'

// Settings sections - colors will be replaced with theme
const SECTIONS = [
  { id: 'appearance', title: 'Appearance' },
  { id: 'audio', title: 'Audio' },
  { id: 'background', title: 'Background' },
]

interface SettingsPageProps {
  onBack: () => void
}

export function SettingsPage({ onBack }: SettingsPageProps) {
  const location = useLocation()
  const [activeSection, setActiveSection] = useState('appearance')
  const targetSectionRef = useRef<string | null>(null)
  const targetReleaseTimeRef = useRef<number>(0)

  const { theme, setTheme, audio, setMusicEnabled, setMusicVolume, setSfxEnabled, setSfxVolume, background, setAnimatedCardsEnabled, nextTrack, previousTrack } = useSettingsStore()
  const { getCurrentTrackIndex, getTotalTracks, playNextTrack, playPreviousTrack } = useAudioManager()
  const currentTheme = useTheme()

  // Handle scroll to update active section
  useEffect(() => {
    const handleScroll = () => {
      const now = Date.now()

      if (targetSectionRef.current && now < targetReleaseTimeRef.current) {
        return
      }

      if (now >= targetReleaseTimeRef.current) {
        targetSectionRef.current = null
      }

      const sections = SECTIONS.map((ch) => document.getElementById(ch.id)).filter(Boolean)

      let maxVisibility = 0
      let activeSection = sections[0]?.id

      for (const section of sections) {
        const rect = section.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        const visibleTop = Math.max(0, rect.top)
        const visibleBottom = Math.min(viewportHeight, rect.bottom)
        const visibleHeight = Math.max(0, visibleBottom - visibleTop)

        const topBonus = rect.top >= 0 && rect.top < 200 ? (200 - rect.top) / 2 : 0
        const score = visibleHeight + topBonus

        if (score > maxVisibility) {
          maxVisibility = score
          activeSection = section.id
        }
      }

      if (activeSection) {
        setActiveSection(activeSection)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      targetSectionRef.current = sectionId
      targetReleaseTimeRef.current = Date.now() + 1600
      setActiveSection(sectionId)

      const headerHeight = 72
      const navHeight = 80
      const offset = headerHeight + navHeight

      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', backgroundColor: '#0a0a0a' }}>
      {/* Subtle floating card background */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          opacity: 0.08,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: '200%',
            height: '200%',
            top: '-50%',
            left: '-50%',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 0L100 50L50 100L0 50Z' fill='%23ffffff' fill-opacity='0.03'/%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          backgroundColor: 'rgba(10, 10, 10, 0.9)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${currentTheme.primaryMuted.replace('0.2', '0.2')}`,
        }}
      >
        <div
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'none',
              border: 'none',
              color: currentTheme.textSecondary,
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = currentTheme.primary
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = currentTheme.textSecondary
            }}
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" strokeWidth={2}>
              <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back
          </button>

          <h1
            style={{
              fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
              fontWeight: 200,
              letterSpacing: '0.15em',
              margin: 0,
              color: currentTheme.primary,
              textShadow: currentTheme.shadow,
            }}
          >
            SETTINGS
          </h1>

          <div style={{ width: '80px' }} />
        </div>
      </motion.header>

      {/* Simple Section Navigation - Horizontal Tabs */}
      <nav
        style={{
          position: 'sticky',
          top: '72px',
          zIndex: 95,
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          padding: '16px 24px',
          backgroundColor: 'rgba(10, 10, 10, 0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${currentTheme.border}`,
          flexWrap: 'wrap',
        }}
      >
        {SECTIONS.map((section, index) => (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            style={{
              padding: '10px 20px',
              background: activeSection === section.id
                ? currentTheme.primaryMuted
                : 'transparent',
              border: activeSection === section.id
                ? `1px solid ${currentTheme.primaryMuted.replace('0.2', '0.4')}`
                : '1px solid transparent',
              borderRadius: '20px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (activeSection !== section.id) {
                e.currentTarget.style.background = currentTheme.primaryMuted.replace('0.2', '0.1')
              }
            }}
            onMouseLeave={(e) => {
              if (activeSection !== section.id) {
                e.currentTarget.style.background = 'transparent'
              }
            }}
          >
            <span
              style={{
                fontSize: '0.8rem',
                fontWeight: 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: activeSection === section.id ? currentTheme.primary : currentTheme.textSecondary,
              }}
            >
              {section.title}
            </span>
          </button>
        ))}
      </nav>

      {/* Main Content */}
      <main
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: '900px',
          margin: '0 auto',
          paddingLeft: '24px',
          paddingRight: '24px',
          paddingTop: '100px',
          paddingBottom: '80px',
        }}
      >
        {/* Appearance Section */}
        <section id="appearance" style={{ marginBottom: '120px', scrollMarginTop: '100px' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div style={{ marginBottom: '40px' }}>
              <div
                style={{
                  width: '60px',
                  height: '3px',
                  backgroundColor: currentTheme.primary,
                  marginBottom: '16px',
                  boxShadow: currentTheme.shadow,
                }}
              />
              <h2
                style={{
                  fontSize: 'clamp(2rem, 5vw, 3rem)',
                  fontWeight: 200,
                  letterSpacing: '0.1em',
                  color: currentTheme.text,
                  margin: 0,
                }}
              >
                APPEARANCE
              </h2>
            </div>

            <div
              style={{
                color: currentTheme.textSecondary,
                lineHeight: 1.8,
                fontSize: '1rem',
              }}
            >
              <p style={{ marginBottom: '32px' }}>
                Customize the visual theme of your game experience.
              </p>

              {/* Theme Selector */}
              <div
                style={{
                  padding: '24px',
                  backgroundColor: currentTheme.card,
                  borderRadius: '12px',
                  border: `1px solid ${currentTheme.primaryMuted.replace('0.2', '0.2')}`,
                  marginBottom: '24px',
                }}
              >
                <h3
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    color: currentTheme.primary,
                    margin: '0 0 20px 0',
                  }}
                >
                  Color Theme
                </h3>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                  }}
                >
                  {(['classic', 'sunset', 'cosmic'] as Theme[]).map((themeOption) => (
                    <button
                      key={themeOption}
                      onClick={() => setTheme(themeOption)}
                      style={{
                        padding: '20px',
                        background: theme === themeOption
                          ? currentTheme.primaryMuted
                          : currentTheme.cardHover,
                        border: theme === themeOption
                          ? `2px solid ${currentTheme.primary}`
                          : `1px solid ${currentTheme.border}`,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                      onMouseEnter={(e) => {
                        if (theme !== themeOption) {
                          e.currentTarget.style.background = currentTheme.cardHover
                          e.currentTarget.style.borderColor = currentTheme.borderHover
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (theme !== themeOption) {
                          e.currentTarget.style.background = currentTheme.cardHover
                          e.currentTarget.style.borderColor = currentTheme.border
                        }
                      }}
                    >
                      {/* Theme preview circle */}
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          background: themes[themeOption].background,
                          border: `2px solid ${themes[themeOption].primary}`,
                        }}
                      />
                      {/* Artist color palette dots */}
                      <div
                        style={{
                          display: 'flex',
                          gap: '4px',
                          marginTop: '4px',
                        }}
                      >
                        {Object.entries(themes[themeOption].artists).map(([artistName, colors]) => (
                          <div
                            key={artistName}
                            style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              background: colors.primary,
                              border: `1px solid ${colors.secondary}`,
                              title: artistName,
                            }}
                          />
                        ))}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: '1rem',
                            fontWeight: 500,
                            color: currentTheme.text,
                            marginBottom: '2px',
                            textTransform: 'capitalize',
                          }}
                        >
                          {themeOption}
                        </div>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: currentTheme.textSecondary,
                          }}
                        >
                          {theme === themeOption ? '✓ Active' : 'Click to select'}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Audio Section */}
        <section id="audio" style={{ marginBottom: '120px', scrollMarginTop: '100px' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div style={{ marginBottom: '40px' }}>
              <div
                style={{
                  width: '60px',
                  height: '3px',
                  backgroundColor: currentTheme.primary,
                  marginBottom: '16px',
                  boxShadow: currentTheme.shadow,
                }}
              />
              <h2
                style={{
                  fontSize: 'clamp(2rem, 5vw, 3rem)',
                  fontWeight: 200,
                  letterSpacing: '0.1em',
                  color: currentTheme.text,
                  margin: 0,
                }}
              >
                AUDIO
              </h2>
            </div>

            <div
              style={{
                color: currentTheme.textSecondary,
                lineHeight: 1.8,
                fontSize: '1rem',
              }}
            >
              <p style={{ marginBottom: '32px' }}>
                Control music and sound effects during gameplay.
              </p>

              {/* Music Settings */}
              <div
                style={{
                  padding: '24px',
                  backgroundColor: currentTheme.card,
                  borderRadius: '12px',
                  border: `1px solid ${currentTheme.border}`,
                  marginBottom: '20px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <div>
                    <h3
                      style={{
                        fontSize: '1.1rem',
                        fontWeight: 500,
                        color: currentTheme.primary,
                        margin: '0 0 4px 0',
                      }}
                    >
                      Background Music
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: currentTheme.textSecondary }}>
                      Ambient jazz/lounge soundtrack
                    </p>
                  </div>
                  <button
                    onClick={() => setMusicEnabled(!audio.musicEnabled)}
                    style={{
                      position: 'relative',
                      width: '56px',
                      height: '32px',
                      background: audio.musicEnabled ? currentTheme.primary : currentTheme.buttonSecondary,
                      border: 'none',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '4px',
                        left: audio.musicEnabled ? '28px' : '4px',
                        width: '24px',
                        height: '24px',
                        background: audio.musicEnabled ? '#000' : '#fff',
                        borderRadius: '50%',
                        transition: 'left 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                  </button>
                </div>

                {audio.musicEnabled && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    style={{ paddingTop: '16px', borderTop: `1px solid ${currentTheme.border}` }}
                  >
                    {/* Track Selector */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '16px',
                        padding: '12px',
                        background: currentTheme.cardHover,
                        borderRadius: '8px',
                      }}
                    >
                      <button
                        onClick={playPreviousTrack}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '36px',
                          height: '36px',
                          background: currentTheme.buttonSecondary,
                          border: `1px solid ${currentTheme.border}`,
                          borderRadius: '8px',
                          color: currentTheme.text,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = currentTheme.primaryMuted
                          e.currentTarget.style.borderColor = currentTheme.primaryMuted.replace('0.2', '0.5')
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = currentTheme.buttonSecondary
                          e.currentTarget.style.borderColor = currentTheme.border
                        }}
                      >
                        <ChevronLeft size={18} />
                      </button>

                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: currentTheme.textSecondary,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            marginBottom: '4px',
                          }}
                        >
                          Now Playing
                        </div>
                        <div
                          style={{
                            fontSize: '0.95rem',
                            color: currentTheme.primary,
                            fontWeight: 500,
                          }}
                        >
                          Track {getCurrentTrackIndex() + 1} of {getTotalTracks()}
                        </div>
                      </div>

                      <button
                        onClick={playNextTrack}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '36px',
                          height: '36px',
                          background: currentTheme.buttonSecondary,
                          border: `1px solid ${currentTheme.border}`,
                          borderRadius: '8px',
                          color: currentTheme.text,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = currentTheme.primaryMuted
                          e.currentTarget.style.borderColor = currentTheme.primaryMuted.replace('0.2', '0.5')
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = currentTheme.buttonSecondary
                          e.currentTarget.style.borderColor = currentTheme.border
                        }}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>

                    {/* Volume Slider */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.85rem',
                          color: currentTheme.textSecondary,
                          minWidth: '60px',
                        }}
                      >
                        Volume
                      </span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={audio.musicVolume * 100}
                        onChange={(e) => setMusicVolume(Number(e.target.value) / 100)}
                        style={{
                          flex: 1,
                          height: '6px',
                          borderRadius: '3px',
                          background: currentTheme.buttonSecondary,
                          outline: 'none',
                          WebkitAppearance: 'none',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = currentTheme.primaryMuted.replace('0.2', '0.4')
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = currentTheme.buttonSecondary
                        }}
                      />
                      <span
                        style={{
                          fontSize: '0.85rem',
                          color: currentTheme.primary,
                          minWidth: '40px',
                          textAlign: 'right',
                          fontWeight: 500,
                        }}
                      >
                        {Math.round(audio.musicVolume * 100)}%
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Sound Effects Settings */}
              <div
                style={{
                  padding: '24px',
                  backgroundColor: currentTheme.card,
                  borderRadius: '12px',
                  border: `1px solid ${currentTheme.border}`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <div>
                    <h3
                      style={{
                        fontSize: '1.1rem',
                        fontWeight: 500,
                        color: currentTheme.primary,
                        margin: '0 0 4px 0',
                      }}
                    >
                      Sound Effects
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: currentTheme.textSecondary }}>
                      Card deals, bids, and game events
                    </p>
                  </div>
                  <button
                    onClick={() => setSfxEnabled(!audio.sfxEnabled)}
                    style={{
                      position: 'relative',
                      width: '56px',
                      height: '32px',
                      background: audio.sfxEnabled ? currentTheme.primary : currentTheme.buttonSecondary,
                      border: 'none',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '4px',
                        left: audio.sfxEnabled ? '28px' : '4px',
                        width: '24px',
                        height: '24px',
                        background: audio.sfxEnabled ? '#000' : '#fff',
                        borderRadius: '50%',
                        transition: 'left 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                  </button>
                </div>

                {audio.sfxEnabled && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    style={{ paddingTop: '16px', borderTop: `1px solid ${currentTheme.border}` }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        marginBottom: '16px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.85rem',
                          color: currentTheme.textSecondary,
                          minWidth: '60px',
                        }}
                      >
                        Volume
                      </span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={audio.sfxVolume * 100}
                        onChange={(e) => setSfxVolume(Number(e.target.value) / 100)}
                        style={{
                          flex: 1,
                          height: '6px',
                          borderRadius: '3px',
                          background: currentTheme.buttonSecondary,
                          outline: 'none',
                          WebkitAppearance: 'none',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = currentTheme.primaryMuted.replace('0.2', '0.4')
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = currentTheme.buttonSecondary
                        }}
                      />
                      <span
                        style={{
                          fontSize: '0.85rem',
                          color: currentTheme.primary,
                          minWidth: '40px',
                          textAlign: 'right',
                          fontWeight: 500,
                        }}
                      >
                        {Math.round(audio.sfxVolume * 100)}%
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </section>

        {/* Background Section */}
        <section id="background" style={{ marginBottom: '120px', scrollMarginTop: '100px' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div style={{ marginBottom: '40px' }}>
              <div
                style={{
                  width: '60px',
                  height: '3px',
                  backgroundColor: currentTheme.primary,
                  marginBottom: '16px',
                  boxShadow: currentTheme.shadow,
                }}
              />
              <h2
                style={{
                  fontSize: 'clamp(2rem, 5vw, 3rem)',
                  fontWeight: 200,
                  letterSpacing: '0.1em',
                  color: currentTheme.text,
                  margin: 0,
                }}
              >
                BACKGROUND
              </h2>
            </div>

            <div
              style={{
                color: currentTheme.textSecondary,
                lineHeight: 1.8,
                fontSize: '1rem',
              }}
            >
              <p style={{ marginBottom: '32px' }}>
                Toggle animated background effects during gameplay.
              </p>

              {/* Animated Cards Toggle */}
              <div
                style={{
                  padding: '24px',
                  backgroundColor: currentTheme.card,
                  borderRadius: '12px',
                  border: `1px solid ${currentTheme.border}`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <h3
                      style={{
                        fontSize: '1.1rem',
                        fontWeight: 500,
                        color: currentTheme.primary,
                        margin: '0 0 4px 0',
                      }}
                    >
                      Animated Cards
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: currentTheme.textSecondary }}>
                      Show floating artwork during gameplay
                    </p>
                  </div>
                  <button
                    onClick={() => setAnimatedCardsEnabled(!background.animatedCardsEnabled)}
                    style={{
                      position: 'relative',
                      width: '56px',
                      height: '32px',
                      background: background.animatedCardsEnabled ? currentTheme.primary : currentTheme.buttonSecondary,
                      border: 'none',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '4px',
                        left: background.animatedCardsEnabled ? '28px' : '4px',
                        width: '24px',
                        height: '24px',
                        background: background.animatedCardsEnabled ? '#000' : '#fff',
                        borderRadius: '50%',
                        transition: 'left 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Footer */}
        <footer
          style={{
            padding: '40px 24px',
            borderTop: `1px solid ${currentTheme.border}`,
            textAlign: 'center',
          }}
        >
          <p style={{ margin: 0, fontSize: '0.85rem', color: currentTheme.textSecondary }}>
            Settings are automatically saved
          </p>
        </footer>
      </main>
    </div>
  )
}

export default SettingsPage
